import React from 'react';
import cn from 'classnames';
import { i18n } from '@lt/localization';
import { Button, CashBack, MobilePopupBottom } from '@lt/components';

import PriceChecker from 'src/routes/Checkout/components/PriceChecker';
import {
  QuestionFill as QuestionIcon,
  Instant as InstantIcon,
} from '@lt/components/icons-react/rebrand';
import CurrencyFormat from '../../../../../../../components/CurrencyFormat';
import BillInfoSkeleton from './BillInfoSkeleton';
import ReactHint from '../../../containers/Hint/HintFactory';
import BestPriceTooltip from '../BestPriceTooltip';

import PriceDetails from '../../../../../components/PriceDetails';
import PriceDetailsItem from '../../../../../components/PriceDetailsItem';
import { EXTRAS, EXTRA_SCOPES } from '../../../../../store/constants/extras';

import { HotPrice } from '../../../../../components/HotPrice';
import type { ExtraItem } from '../../../../../types/extras';
import { calculateCashback } from '../../../../../utils/orderUtils';
import type { IBillInfo } from './types';

import styles from './billInfo.module.css';

interface IState {
  isTooltipOpened: boolean;
}
class BillInfo extends React.PureComponent<IBillInfo, IState> {
  private priceRef: React.RefObject<HTMLDivElement>;

  constructor(props) {
    super(props);

    this.handleBestPriceClicked = this.handleBestPriceClicked.bind(this);
    this.priceRef = React.createRef();
    this.state = { isTooltipOpened: false };
  }

  handleBestPriceClicked = () => {
    const { isMobile } = this.props;
    const { isTooltipOpened } = this.state;
    if (!isMobile) return;
    this.setState({ isTooltipOpened: !isTooltipOpened });
  };

  render() {
    const {
      fetching,
      minimalFuelCharge,
      netPrice,
      mandatoryExtras,
      isManualPackage,
      skipFlights,
      isMobile,
      oldPrice,
      discount,
      isArchived,
      bonuses,
      activateCashbackForPartner,
      cashbackLabel,
      cashbackIconWebp,
      cashbackIconPng,
      cashbackTooltipText,
      labelColor,
      textColor,
    } = this.props;

    const extraPrice = mandatoryExtras.reduce(
      (extrasPrice, extra) => extrasPrice + (extra.price || 0),
      0,
    );

    const fullPrice = netPrice + minimalFuelCharge + extraPrice;

    const shouldGetCashback = activateCashbackForPartner && !isArchived;

    const cashbackFromPurchase = shouldGetCashback
      ? calculateCashback(bonuses, fullPrice)
      : 0;

    const extras = [
      { name: i18n.t('checkout.billInfo.extras'), price: minimalFuelCharge },
      /** Фильтруем услугу конкретизации рейса, так как она не добавляется к самому дешевому рейсу */
      ...mandatoryExtras.filter(
        (extra) => extra.code_name !== EXTRAS.FLIGHT_LOCK,
      ),
    ] as ExtraItem[];

    const tooltipProps = isMobile
      ? {}
      : {
          'data-warranty': true,
        };

    const { isTooltipOpened } = this.state;

    return (
      <div
        className={cn(styles.container, { [styles.mobileContainer]: isMobile })}
      >
        <MobilePopupBottom
          onClick={() => {
            this.setState({ isTooltipOpened: false });
          }}
          visible={isTooltipOpened}
          options={{ showHandlebar: true }}
        >
          <BestPriceTooltip isMobile />
        </MobilePopupBottom>
        {fetching ? (
          <BillInfoSkeleton />
        ) : (
          <div>
            <ReactHint
              events={{ click: true }}
              attribute="data-warranty"
              className="tooltip-payment react-hint"
              onRenderContent={() =>
                !isMobile && <BestPriceTooltip isMobile={false} />
              }
            />
            <h2 className={styles.title}>
              <span>{i18n.t('checkout.billInfo.bestPrice')}</span>
              <button
                type="button"
                className={styles.titleButton}
                onClick={this.handleBestPriceClicked}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...tooltipProps}
              >
                <QuestionIcon className={styles.titleHintIcon} />
              </button>
            </h2>
            <div className={styles.totalContainer}>
              <h3
                className={styles.totalPriceContainer}
                ref={this.priceRef}
                data-price-changed
              >
                <InstantIcon className={styles.totalPriceInstantIcon} />
                <CurrencyFormat
                  className={styles.totalPrice}
                  value={fullPrice}
                />
                {isMobile && <HotPrice value={oldPrice} discount={discount} />}
              </h3>
              {cashbackFromPurchase ? (
                <CashBack
                  count={`+${cashbackFromPurchase}`}
                  isMobile={isMobile}
                  cashbackTooltipText={cashbackTooltipText}
                  labelColor={labelColor}
                  textColor={textColor}
                  cashbackIconWebp={cashbackIconWebp}
                  cashbackIconPng={cashbackIconPng}
                  label={cashbackLabel}
                />
              ) : null}
            </div>
            <div className={styles.summarizeContainer}>
              <PriceDetails>
                <PriceDetailsItem
                  label={i18n.t('checkout.billInfo.baseCost_package')}
                >
                  {!isMobile && (
                    <HotPrice value={oldPrice} discount={discount} />
                  )}
                  <CurrencyFormat value={netPrice} />
                </PriceDetailsItem>
                {extras.map((extra) => {
                  const { name, price, scope, mandatory } = extra;
                  const showAsMandatory =
                    mandatory && scope !== EXTRA_SCOPES.LEVEL_TRAVEL;
                  const label = showAsMandatory
                    ? `${name} (${i18n.t('checkout.billInfo.mandatory')})`
                    : name;
                  return (
                    <PriceDetailsItem key={name} label={label} price={price} />
                  );
                })}
              </PriceDetails>
              {!isArchived && (
                <>
                  <div className={styles.summarizeSeparator} />
                  <PriceChecker className={styles.summarizeCounter} />
                </>
              )}
            </div>

            {!isArchived && isManualPackage && (
              <Button
                className={styles.skipFlightsButton}
                onClick={skipFlights}
                size="none"
                primary
              >
                {i18n.t('checkout.billInfo.withoutFlight')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default BillInfo;
